import React, { memo } from 'react';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import isEqual from 'react-fast-compare';
import { DeferredRender, DeferredRenderModes } from '@zillow/react-deferred-hydration';
import { Advert, refreshSlot, destroySlot } from '@zillow/mst-display-ad';

import {
    deviceType,
    onSlotRenderEnded,
    onMessage,
    getSlotSize,
    isAdRestricted,
    onSlotOnload,
    updateVersValue,
    shouldAdHavePrebid,
} from '../util/common';
import { AD_NETWORK, NAV_AD_TYPE } from '../constants';

import {
    getIsMobileLayout,
    getIsTablet,
    getHasAdGptScriptLoaded,
    getHasAdPrebidScriptLoaded,
    getHasAdPrebidScriptFailed,
} from '../util/getFromState';
import { useWindowEvent } from '../../../hooks/useWindowEvent';
import { useEffectOnce } from '../../../hooks/useEffectOnce';

const SearchAdCmp = ({
    slot,
    adType,
    onSlotRenderEndedSlot,
    iabEnabled,
    className,
    targetDiv,
    isTablet,
    isMobile,
    targets,
    versOn,
    variant,
    isPrebidTrialOn,
    adPrebidScriptFailed,
    hasBeenVisible,
}) => {
    const device = deviceType({ isTablet, isMobile });
    const adSizes = getSlotSize(adType, iabEnabled);
    const path = `/${AD_NETWORK}/${slot}`;
    const isRestricted = isAdRestricted(path);
    const adGptScriptLoaded = useSelector(getHasAdGptScriptLoaded);
    const adPrebidScriptLoaded = useSelector(getHasAdPrebidScriptLoaded);
    const pageType = targets?.listtp;
    const shouldHavePrebid = shouldAdHavePrebid({ slot, adPrebidScriptFailed, isPrebidTrialOn });
    const updatedTargets = updateVersValue(targets, versOn);
    useEffectOnce(() => () => {
        destroySlot(targetDiv);
    });

    const inventory = {
        targets: updatedTargets,
        path,
        isRestricted,
        device,
        adSizes,
        targetDiv,
        pageType,
        collapseAfterRequest: true,
        collapseBeforeRequest: false,
        isPrebid: shouldHavePrebid,
    };

    useWindowEvent({
        eventName: 'message',
        callback: onMessage,
        args: [adType, onSlotRenderEndedSlot],
    });

    if (!adGptScriptLoaded && !adPrebidScriptLoaded) {
        return null;
    }

    return (
        <DeferredRender
            mode={DeferredRenderModes.RENDER_CLIENT_SIDE_ON_IMMEDIATE}
            data-testid="mst-ad-display"
        >
            {!hasBeenVisible || (
                <Advert
                    adInventory={inventory}
                    className={className}
                    targetDiv={targetDiv}
                    targets={updatedTargets}
                    variant={variant}
                    prebidonload={adPrebidScriptLoaded}
                    gptonload={adGptScriptLoaded}
                    isSRA
                    blockGptLib
                    blockPrebidLib
                    isPrebidAllowed={shouldHavePrebid}
                    prebidPageType={shouldHavePrebid ? 'zillow_srp' : null}
                    eventList={['slotRenderEnded', 'impressionViewable', 'slotOnload']}
                    onSlotRenderEnded={e =>
                        onSlotRenderEnded(e, adType, onSlotRenderEndedSlot, path)
                    }
                    onSlotOnload={e => onSlotOnload(e)}
                />
            )}
        </DeferredRender>
    );
};

const mapStateToProps = state => ({
    isMobile: getIsMobileLayout(state),
    isTablet: getIsTablet(state),
    adPrebidScriptFailed: getHasAdPrebidScriptFailed(state) && !getHasAdPrebidScriptLoaded(state),
});

const destroyablePropsMatch = (prevProps, nextProps) =>
    prevProps.slot === nextProps.slot &&
    prevProps.iabEnabled === nextProps.iabEnabled &&
    prevProps.renderAd === nextProps.renderAd &&
    prevProps.isTablet === nextProps.isTablet &&
    prevProps.isMobile === nextProps.isMobile &&
    prevProps.isPrebidTrialOn === nextProps.isPrebidTrialOn &&
    prevProps.adPrebidScriptFailed === nextProps.adPrebidScriptFailed;

const SearchAd = memo(
    props => <SearchAdCmp {...props} />,
    (prevProps, nextProps) => {
        const propsMatch = destroyablePropsMatch(prevProps, nextProps);
        if (!propsMatch) {
            destroySlot(prevProps.targetDiv);
            return false;
        }

        const shouldRender = !isEqual(prevProps.targets, nextProps.targets);
        const shouldHavePrebid = shouldAdHavePrebid(nextProps);
        if (shouldRender) {
            const targetClone = { ...nextProps.targets };
            const refreshedTargets = updateVersValue(targetClone, nextProps.versOn);
            refreshSlot(nextProps.targetDiv, refreshedTargets, { isPrebid: shouldHavePrebid });
        }
        return propsMatch && prevProps.hasBeenVisible === nextProps.hasBeenVisible;
    }
);

SearchAdCmp.propTypes = {
    slot: PropTypes.string,
    adType: PropTypes.string,
    onSlotRenderEndedSlot: PropTypes.func,
    iabEnabled: PropTypes.bool,
    className: PropTypes.string,
    isTablet: PropTypes.bool,
    isMobile: PropTypes.bool,
    targetDiv: PropTypes.string.isRequired,
    targets: PropTypes.shape(),
    versOn: PropTypes.string,
    variant: PropTypes.string,
    isPrebidTrialOn: PropTypes.bool,
    adPrebidScriptFailed: PropTypes.bool,
};

SearchAdCmp.defaultProps = {
    adType: NAV_AD_TYPE,
};

export default connect(mapStateToProps)(SearchAd);
